// Import hooks.
import {
    useInputCreatePassword,
    UseInputCreatePasswordResults,
    UseInputEmailResults,
    useInputEmail,
    UseInputFormResult,
    UseInputTextResults,
    useInputText,
} from '@app/hooks/view/input';
// Import props.
import {
    FormProducerSignupProps,
} from '@app/props';

interface UseSubformProducerPrimaryContactParameters {
    id: string;
    showValidations?: boolean;
    props: FormProducerSignupProps;
}

interface UseSubformProducerPrimaryContactResults {
    firstNameInput: UseInputTextResults;
    lastNameInput: UseInputTextResults;
    emailInput: UseInputEmailResults;
    newPasswordInput: UseInputCreatePasswordResults;
    flagAllInputsToShowValidations: () => void;
    checkFormValidationWithFocus: () => boolean;
}

const useSubformProducerPrimaryContact = ( params: UseSubformProducerPrimaryContactParameters ): UseSubformProducerPrimaryContactResults => {
    
	// #region exported methods

    /**
     * Flags all inputs to show the validation messages.
     **/
    const flagAllInputsToShowValidations = (): void => {
        firstNameInput.changeShowValidationFlag(true);
        lastNameInput.changeShowValidationFlag(true);
        emailInput.changeShowValidationFlag(true);
        newPasswordInput.passwordInput.changeShowValidationFlag(true);
        newPasswordInput.confirmPasswordInput.changeShowValidationFlag(true);
    };

    /**
     * Method to check form validation with focus.
     **/
    const checkFormValidationWithFocus = (): boolean => {
        return (
			checkInputValidationWithFocus<string>(firstNameInput)
            && checkInputValidationWithFocus<string>(lastNameInput)
            && checkInputValidationWithFocus<string>(emailInput)
            && checkInputValidationWithFocus<string>(newPasswordInput.passwordInput)
            && checkInputValidationWithFocus<string>(newPasswordInput.confirmPasswordInput)
        );
    };

    // #endregion exported methods

    // #region private methods

    /**
     * Validates an individual input with focus.
     * @param input
     */
    const checkInputValidationWithFocus = <T>(input: UseInputFormResult<T>): boolean => {
        const isValid = input.isInputValid;
        if (!isValid) {
            input.focusToInput();
        }
        return isValid;
    };

    //#region id generators

    const generateIdForForm = (): string => {
        return params.id;
    };

    const generateIdForFirstName = (): string => {
        return `${generateIdForForm()}-firstName`;
    };
    
    const generateIdForLastName = (): string => {
        return `${generateIdForForm()}-lastName`;
    };

    const generateIdForEmail = (): string => {
        return `${generateIdForForm()}-email`;
    };

    const generateIdForNewPassword = (): string => {
        return `${generateIdForForm()}-newPassword`;
    }

    //#endregion id generators

    // #endregion private methods

    // #region variables

    const firstNameInput: UseInputTextResults = useInputText({
        id: generateIdForFirstName(),
        label: 'First Name',
        required: true,
        disabled: false,
        value: String.empty(),
        showValidation: params.showValidations
    });

    const lastNameInput: UseInputTextResults = useInputText({
        id: generateIdForLastName(),
        label: 'Last Name',
        required: true,
        disabled: false,
        value: String.empty(),
        showValidation: params.showValidations
    });

    const emailInput: UseInputEmailResults = useInputEmail({
		id: generateIdForEmail(),
		label: 'Email',
        infoText: 'Please, provide an address that is regularly monitored.  This is how we communicate with you about policies, commissions, underwriter decisions, upcoming payments, administrative needs, etc.',
		required: true,
		disabled: false,
		value: String.empty(),
        showValidation: params.showValidations
	});

    const newPasswordInput: UseInputCreatePasswordResults = useInputCreatePassword({
        id: generateIdForNewPassword(),
        required: true,
        disabled: false,
        showValidation: params.showValidations
    });

    // #endregion variables

    return {
        firstNameInput: firstNameInput,
        lastNameInput: lastNameInput,
        emailInput: emailInput,
        newPasswordInput: newPasswordInput,
        flagAllInputsToShowValidations: flagAllInputsToShowValidations,
        checkFormValidationWithFocus: checkFormValidationWithFocus,
    };
};

// Export parameters.
export { UseSubformProducerPrimaryContactParameters };

// Export results.
export { UseSubformProducerPrimaryContactResults };

export default useSubformProducerPrimaryContact;
